import { defineStore } from 'pinia';
import api from '@/services/api';

export const useRatingsStore = defineStore('ratings', {
  state: () => ({
    ratings: [],
    loading: false,
    error: null,
    // Estado para uma avaliação específica (pode ser útil)
    currentRating: null,
  }),

  actions: {
    /**
     * Busca todas as avaliações de um fornecedor específico.
     * @param {string} supplierId - O ID do fornecedor.
     */
    async fetchRatingsBySupplier(supplierId) {
      this.loading = true;
      this.error = null;
      try {
        const response = await api.ratings.getBySupplierId(supplierId);
        if (response.data && response.data.success) {
          this.ratings = response.data.data;
        } else {
          this.ratings = [];
        }
      } catch (error) {
        console.error('Erro ao buscar avaliações por fornecedor:', error);
        this.error = 'Não foi possível carregar as avaliações.';
      } finally {
        this.loading = false;
      }
    },

    /**
     * Cria uma nova avaliação.
     * @param {object} ratingData - Os dados da avaliação { supplierId, rating, comment }.
     * @returns {Promise<boolean>} - Retorna true em caso de sucesso.
     */
    async addRating(ratingData) {
      this.loading = true;
      this.error = null;
      try {
        const response = await api.ratings.create(ratingData);
        if (response.data && response.data.success) {
          // Adicionar a nova avaliação à lista local
          this.ratings.unshift(response.data.data);
          return true;
        }
        return false;
      } catch (error) {
        console.error('Erro ao adicionar avaliação:', error);
        this.error = error.response?.data?.message || 'Erro ao salvar a avaliação.';
        throw error; // Lançar o erro para que o componente possa tratá-lo
      } finally {
        this.loading = false;
      }
    },

    /**
     * Atualiza uma avaliação existente.
     * @param {string} ratingId - O ID da avaliação.
     * @param {object} updateData - Os dados a serem atualizados { rating, comment }.
     * @returns {Promise<boolean>}
     */
    async updateRating(ratingId, updateData) {
      this.loading = true;
      this.error = null;
      try {
        const response = await api.ratings.update(ratingId, updateData);
        if (response.data && response.data.success) {
          // Atualizar a avaliação na lista local
          const index = this.ratings.findIndex((r) => r.id === ratingId);
          if (index !== -1) {
            this.ratings[index] = { ...this.ratings[index], ...response.data.data };
          }
          return true;
        }
        return false;
      } catch (error) {
        console.error('Erro ao atualizar avaliação:', error);
        this.error = error.response?.data?.message || 'Erro ao atualizar a avaliação.';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Remove uma avaliação.
     * @param {string} ratingId - O ID da avaliação a ser removida.
     * @returns {Promise<boolean>}
     */
    async deleteRating(ratingId) {
      this.loading = true;
      this.error = null;
      try {
        const response = await api.ratings.delete(ratingId);
        if (response.data && response.data.success) {
          // Remover a avaliação da lista local
          this.ratings = this.ratings.filter((r) => r.id !== ratingId);
          return true;
        }
        return false;
      } catch (error) {
        console.error('Erro ao remover avaliação:', error);
        this.error = error.response?.data?.message || 'Erro ao remover a avaliação.';
        throw error;
      } finally {
        this.loading = false;
      }
    },
  },
}); 